import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearAppointmentAuditData,
    clearNewAppointment,
    loadAppointmentAuditData,
    loadAppointmentSlot,
    setOpenAppointmentSchedulerPanel,
    setParticipantsCurrentAppointment
} from '../../../store/calendarService';
import { selectUsersForNamesById } from '../../../store/userSelectors';
import { loadTeam, loadUsersMinimalDetails } from '../../../store/userService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { getNameFromId } from '../../../utils/directusFunctions';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import Panel from '../../ui/panel/Panel';
import ParticipantSelectedPills from '../components/ParticipantSelectedPills';
import StartAppointmentSlotsRows from '../components/StartAppointmentSlotsRows';
import {
    auditRoles,
    cancelledStatuses,
    getServiceIds,
    participantAccessRoles,
    viewAdviserRoles
} from '../utils/calendarUtils';

import form from '../../formElements/formElementStyles/commonFormElements.module.css';
import sidePanel from '../styles/schedulePanel.module.css';

const ParticipantAppointmentSlotsView = ({ currentEvent, currentLocation }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = { id: '', service: '' };
    const [newEntry, setNewEntry] = useState(initialState);
    const [appointmentAdviser, setAppointmentAdviser] = useState({});
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [reservedAppointments, setReservedAppointments] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [auditParticipantId, setAuditParticipantId] = useState('');
    const [auditModalHeadingLabel, setAuditModalHeadingLabel] = useState('');
    const [isAuditDisabled, setIsAuditDisabled] = useState(false);
    const [isAdviserHidden, setIsAdviserHidden] = useState(false);
    const [hasParticipantAccess, setHasParticipantAccess] = useState({});

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector(selectUsersForNamesById);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const allTeams = useSelector((state) => state.entities.userService.allTeams);
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const appointment = useSelector(
        (state) => state.entities.calendarService.participantsCurrentAppointment
    );

    // USEEFFECTS
    useEffect(() => {
        dispatch(loadAppointmentSlot(currentEvent.id));
    }, []);

    useEffect(() => {
        if (!roles?.length) return;
        setIsAuditDisabled(!hasRole(auditRoles, roles));
        setIsAdviserHidden(
            !hasRole(viewAdviserRoles, roles) && currentEvent.userId !== loggedInUser.id
        );
    }, [roles]);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== currentEvent.id) return;
        if (appointment.participantIds?.length > 0) {
            const selected = appointment.participantIds.map((el) =>
                currentParticipant.id === el
                    ? currentParticipant
                    : { id: el, hidden: 'slot filled' }
            );
            setSelectedParticipants(selected);
        }
        const team = allTeams.find((el) => el.id === appointment?.teamId);
        !team && dispatch(loadTeam(appointment.teamId));
    }, [appointment]);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== currentEvent.id) return;
        if (!(appointment.userId in users)) dispatch(loadUsersMinimalDetails([appointment.userId]));
        else setAppointmentAdviser(users[appointment.userId]);
    }, [users, appointment]);

    useEffect(() => {
        if (!currentEvent || !(currentEvent?.serviceId in serviceDetailsById)) return;
        setNewEntry({
            id: currentEvent.id,
            service: serviceDetailsById[currentEvent.serviceId].name
        });
    }, [serviceDetailsById]);

    useEffect(() => {
        if (
            !('id' in appointment) ||
            appointment.id !== currentEvent.id ||
            selectedParticipants.length < 1
        )
            return;
        const statuses = selectedParticipants.reduce(
            (acc, cur) => ({
                ...acc,
                [cur.id]:
                    appointment?.attendances.find((entry) => entry.participantId === cur.id)
                        ?.status || 'Not known'
            }),
            {}
        );
        setStatuses(statuses);
        setReservedAppointments(
            Object.values(statuses).filter((el) => !cancelledStatuses.includes(el))
        );

        const usersServiceIds = getServiceIds(loggedInUser);
        const accesses = selectedParticipants.reduce(
            (acc, cur) => ({
                ...acc,
                [cur.id]:
                    usersServiceIds.some((el) => el === cur.serviceId) &&
                    hasRole(participantAccessRoles, roles)
            }),
            {}
        );
        setHasParticipantAccess(accesses);
    }, [selectedParticipants, appointment, loggedInUser, roles]);

    // HELPER FNS
    const configTitleRow = () => {
        return (
            <>
                {newEntry.service}
                <span>
                    {reservedAppointments.length} / {appointment?.numberOfSlots}
                </span>
            </>
        );
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setParticipantsCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setAuditModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onSearchAuditItems = (label, modalHeadingLabel = '', participantId = '') => {
        if (newEntry.id) dispatch(loadAppointmentAuditData('appointment-slot', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setAuditModalHeadingLabel(modalHeadingLabel ?? label);
        auditParticipantId !== participantId && setAuditParticipantId(participantId);
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        disabled={isAuditDisabled}
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                </div>
                <div className={sidePanel.formWrapper}>
                    <StaticField content={configTitleRow()} customClass="underlinedContent" />

                    <StaticField
                        id="teamId"
                        label="Team"
                        content={getNameFromId(allTeams, appointment?.teamId)}
                    />

                    {!isAdviserHidden && (
                        <StaticField
                            id="adviser"
                            label="Adviser"
                            content={appointmentAdviser?.loginEmailAddress || ''}
                        />
                    )}

                    <StaticField label="Appointment Type" content={appointment?.type} />

                    <StaticField
                        label="Location"
                        content={currentLocation}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />

                    {currentEvent?.additionalDetails && (
                        <StaticField
                            id="additionalDetails"
                            label="Additional Details"
                            content={currentEvent.additionalDetails}
                        />
                    )}

                    <StaticField
                        label="Start Time"
                        content={appointment?.startTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Start Time')}
                    />

                    <StaticField
                        label="End Time"
                        content={appointment?.endTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('End Date', 'End Time')}
                    />

                    <StaticField
                        label="Date"
                        content={reverseFormatDate(appointment?.date)}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                    />

                    <div className={`${form.formLabel} ${sidePanel.pillsView}`}>
                        {selectedParticipants.length ? 'Participants' : ''}
                    </div>

                    <ParticipantSelectedPills
                        participants={selectedParticipants}
                        statuses={statuses}
                        isDisabled={true}
                        areAnySlotsResolved={true}
                        isInPast={true}
                        roles={roles}
                    />

                    <div className={sidePanel.slotRowsView}>
                        <StartAppointmentSlotsRows
                            participants={selectedParticipants}
                            appointment={appointment}
                            disabled={true}
                            isUpdateDisabled={true}
                            hasParticipantAccess={hasParticipantAccess}
                            onPanelClose={onPanelClose}
                            statuses={statuses}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                        />
                    </div>
                </div>
            </Panel>
            {showAuditModal && (
                <AuditAppointmentModal
                    isOpen={showAuditModal}
                    label={auditLabel}
                    modalHeadingLabel={auditModalHeadingLabel}
                    participantId={auditParticipantId}
                    onClose={onAuditModalClose}
                />
            )}
        </>
    );
};

ParticipantAppointmentSlotsView.propTypes = {
    currentEvent: PropTypes.object,
    currentLocation: PropTypes.string
};

export default ParticipantAppointmentSlotsView;
